// features/projects/templates/professional/widgets/track-record.js
// ═══════════════════════════════════════════════════════════
// TRACK RECORD (CALIBRATION) — Tier-A widget
//
// Q: "Is the methodology actually calibrated? What's the hit
//     rate, and how soon will I find out?"
// Pending/due/resolved counts + hit rate (when n>=10) +
// next-resolution date.
// ═══════════════════════════════════════════════════════════

import { L, FONT } from '../tokens';
import { useWidgetData } from './data-provider';
import { DYNAMICS } from '../../../../../lib/continuum/simulation/methodology';
import { WidgetShell, LayerSection, LayerAction, CaveatStrip } from './findings-status';

const DYN_BY_KEY = Object.fromEntries(DYNAMICS.map(d => [d.key, d]));

export default function TrackRecordWidget() {
  const { loading, predictions } = useWidgetData();

  if (loading) {
    return <WidgetShell label="Track Record"><div style={{ fontSize: 12, color: L.dim, fontStyle: 'italic', fontFamily: FONT.sans }}>Loading…</div></WidgetShell>;
  }

  // Empty state — short paragraph, no labeled-section stack
  if (!predictions || predictions.length === 0) {
    return (
      <WidgetShell label="Track Record (Calibration)">
        <div style={{ fontSize: 12, color: L.mid, lineHeight: 1.55, fontFamily: FONT.sans }}>
          No predictions registered yet. Calibration begins when predictions are logged with confidence labels and resolution dates.
        </div>
      </WidgetShell>
    );
  }

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const pending = predictions.filter(p => p.status === 'pending');
  const due = pending.filter(p => new Date(p.resolution_date) <= today);
  const resolved = predictions.filter(p =>
    p.status?.startsWith('resolved') || p.status === 'inconclusive'
  );
  const confirmed = predictions.filter(p => p.status === 'resolved-true').length;
  const refuted = predictions.filter(p => p.status === 'resolved-false').length;
  const upcoming = pending
    .filter(p => new Date(p.resolution_date) > today)
    .slice().sort((a, b) => new Date(a.resolution_date) - new Date(b.resolution_date))[0];

  const story = composeStory(pending.length, due.length, resolved.length, upcoming);
  const takeaway = composeTakeaway(predictions.length, resolved.length, confirmed, refuted);
  const action = composeAction(due, upcoming);
  const caveat = composeCaveat(predictions.length, resolved.length);

  return (
    <WidgetShell label="Track Record (Calibration)">
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'baseline',
        marginBottom: 6,
      }}>
        <Stat color={L.gold} count={pending.length} label="pending" />
        {due.length > 0 && <Stat color={L.terra} count={due.length} label="due" />}
        <Stat color={L.sage} count={resolved.length} label="resolved" />
      </div>
      {predictions.length > 0 && (
        <div style={{ fontSize: 11, color: L.mid, fontFamily: FONT.sans, lineHeight: 1.55 }}>
          Hit rate: {resolved.length === 0 ? '—' : `${Math.round((confirmed / resolved.length) * 100)}% (n=${resolved.length})`}
          {upcoming && (
            <>
              {' · '}
              Next resolution: {formatDate(upcoming.resolution_date)} (
              {DYN_BY_KEY[upcoming.dynamic_key]?.label || upcoming.dynamic_key}
              )
            </>
          )}
        </div>
      )}
      <LayerSection label="Story">{story}</LayerSection>
      <LayerSection label="Takeaway" accent={L.gold}>{takeaway}</LayerSection>
      {action && <LayerAction accent={action.accent || L.sage} label={action.label} why={action.why} />}
      {caveat && <CaveatStrip text={caveat} />}
    </WidgetShell>
  );
}

function Stat({ color, count, label }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
      <span style={{
        fontFamily: FONT.mono, fontSize: 22, color, fontWeight: 300, letterSpacing: '-0.01em',
      }}>{count}</span>
      <span style={{ fontSize: 11, color: L.mid, fontFamily: FONT.sans }}>{label}</span>
    </span>
  );
}

function composeStory(pending, due, resolved, upcoming) {
  if (pending + resolved === 0) return 'No predictions registered yet. The track record is empty — calibration begins when predictions are logged with confidence labels and resolution dates.';
  if (resolved === 0 && due === 0) {
    return `${pending} prediction${pending === 1 ? '' : 's'} pending; calibration is in build-up phase. Hit rates only become statistically meaningful at n ≥ 10 resolved per confidence tier${upcoming ? `; the first will resolve ${formatDate(upcoming.resolution_date)}` : ''}.`;
  }
  if (due > 0) return `${due} prediction${due === 1 ? ' has' : 's have'} reached its resolution date and ${due === 1 ? 'awaits' : 'await'} a verdict. Resolving promptly preserves the integrity of the calibration record.`;
  return `${resolved} resolved · ${pending} still pending. The track record is starting to take shape.`;
}
function composeTakeaway(total, resolved, confirmed, refuted) {
  if (total === 0) return 'A methodology without forward predictions has no falsification record — every claim is post-hoc explanation. Logging predictions transforms claims into testable bets.';
  if (resolved === 0) return 'The calibration claim is a future commitment, not a current fact. Confidence labels are research promises that must be redeemed by resolution dates.';
  if (resolved < 10) return `${resolved} resolved prediction${resolved === 1 ? ' is' : 's are'} not enough to claim calibration — at this n, hit rates are anecdotes. The track record becomes evidence at n ≥ 10.`;
  const pct = Math.round((confirmed / resolved) * 100);
  return `${pct}% hit rate across ${resolved} resolved predictions. ${confirmed} confirmed, ${refuted} refuted. This is now an actual calibration claim, not a promise.`;
}
function composeAction(due, upcoming) {
  if (due.length > 0) {
    return {
      label: `Resolve ${due.length} due prediction${due.length === 1 ? '' : 's'}`,
      why: 'Resolution dates have passed. Mark confirmed / refuted / inconclusive — each resolution populates the calibration record.',
      accent: L.terra,
    };
  }
  if (upcoming) {
    const days = Math.round((new Date(upcoming.resolution_date) - new Date()) / 86400000);
    if (days <= 30) {
      return {
        label: `First resolution upcoming in ${days} day${days === 1 ? '' : 's'}`,
        why: `Resolution date ${formatDate(upcoming.resolution_date)}. Begin gathering evidence now.`,
        accent: L.gold,
      };
    }
  }
  return null;
}
function composeCaveat(total, resolved) {
  if (total === 0) return 'Calibration cannot begin without predictions. Until then, every confidence label is unanchored.';
  if (resolved < 10) return `Calibration is in build-up phase (n=${resolved}). Treat hit-rate displays as placeholders. Statistical interpretation requires n ≥ 10 resolved per tier.`;
  return 'Hit rate is the methodology\'s track record — but selection effects matter. Vague resolution criteria or motivated reasoning at resolution time can inflate calibration.';
}
function formatDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(); } catch { return String(d); }
}
